import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Eye, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { schoolConfigAPI } from '../services/api';

const BASE_URL = 'http://localhost:5000';
const GOLD      = '#c9a961';
const GOLD_DARK = '#2c2408';

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm]       = useState({ email: '', password: '' });
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(false);
  // Cache branding so returning visitors never see the fallback flash
  const [branding, setBranding] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('login_branding')) || {}; }
    catch { return {}; }
  });

  useEffect(() => {
    schoolConfigAPI.getPublicConfig()
      .then(r => {
        setBranding(r.data);
        sessionStorage.setItem('login_branding', JSON.stringify(r.data));
      })
      .catch(() => {});
  }, []);

  const design     = branding.login_design || 'premium_dark';
  const schoolName = branding.school_name || 'The Kulish School';
  const logoUrl    = branding.school_logo ? `${BASE_URL}${branding.school_logo}` : null;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error('Please enter email and password'); return; }
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const Logo = ({ size = 56 }) => (
    <div
      className="rounded-full bg-white flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {logoUrl
        ? <img src={logoUrl} alt="School logo" className="w-full h-full object-contain p-1.5" />
        : <GraduationCap size={size * 0.5} className="text-primary-800" />}
    </div>
  );

  // Plain render functions (not components) so inputs keep focus across re-renders
  const renderForm = (dark = false) => {
    const label = dark ? 'block text-sm font-medium text-white/80 mb-1.5'  : 'block text-sm font-medium text-slate-700 mb-1.5';
    const input = dark
      ? 'w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all'
      : 'w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all';
    const btn = dark
      ? 'w-full py-3 font-semibold rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 shadow-lg hover:brightness-110'
      : 'w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 shadow-lg hover:shadow-primary-300/50';
    return (
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className={label}>Email Address</label>
          <input
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className={input}
            placeholder="you@kulishschool.com"
          />
        </div>
        <div>
          <label className={label}>Password</label>
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              autoComplete="current-password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className={`${input} pr-11`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShow(s => !s)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${dark ? 'text-white/40 hover:text-white/70' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className={btn}
          style={dark ? { background: GOLD, color: GOLD_DARK } : undefined}
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : 'Sign In'}
        </button>
      </form>
    );
  };

  // ── Design E: Premium dark monogram (default) ─────────────
  if (design === 'premium_dark') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0c2233] px-6 py-10">
        <Logo size={76} />
        <h1 className="text-white text-3xl font-serif tracking-wide mt-5 text-center">{schoolName}</h1>
        <div className="h-0.5 w-12 my-3" style={{ background: GOLD }} />
        <p className="text-[#8fa8b8] text-xs uppercase tracking-[0.22em] mb-9">Kulish Suite</p>
        <div className="w-full max-w-sm">
          {renderForm(true)}
        </div>
        <p className="text-[#5c7a90] text-xs mt-8">Jaipur, Rajasthan</p>
      </div>
    );
  }

  // ── Design D: Enterprise split ─────────────────────────────
  if (design === 'enterprise_split') {
    return (
      <div className="min-h-screen flex">
        <div className="hidden lg:flex flex-col justify-between flex-[1.3] bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 p-12">
          <div className="flex items-center gap-4">
            <Logo size={52} />
            <div>
              <p className="text-white font-display font-bold text-xl leading-tight">{schoolName}</p>
              <p className="text-primary-300 text-sm">Kulish Suite</p>
            </div>
          </div>
          <div>
            <p className="text-white text-2xl font-display font-semibold leading-snug max-w-md">
              "Learning today, leading tomorrow"
            </p>
            <p className="text-primary-300 text-sm mt-3">{schoolName}, Jaipur</p>
          </div>
        </div>
        <div className="flex-1 lg:max-w-xl bg-white flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-3 mb-8 lg:hidden">
              <Logo size={44} />
              <div>
                <p className="font-display font-bold text-slate-800">{schoolName}</p>
                <p className="text-slate-400 text-xs">Kulish Suite</p>
              </div>
            </div>
            <h2 className="text-2xl font-display font-bold text-slate-800">Welcome Back</h2>
            <p className="text-slate-500 text-sm mt-1 mb-8">Sign in to your dashboard</p>
            {renderForm()}
          </div>
        </div>
      </div>
    );
  }

  // ── Design B: Centered minimal ─────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6 py-16">
      <div className="relative bg-white border border-slate-200 rounded-2xl shadow-sm px-8 pb-8 pt-14 w-full max-w-sm">
        <div className="absolute -top-9 left-1/2 -translate-x-1/2">
          <div className="rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            <Logo size={64} />
          </div>
        </div>
        <div className="text-center mb-7">
          <h2 className="text-xl font-display font-bold text-slate-800">{schoolName}</h2>
          <p className="text-slate-400 text-xs uppercase tracking-widest mt-1">Kulish Suite</p>
        </div>
        {renderForm()}
        <p className="text-center text-xs text-slate-300 mt-6">© {schoolName} · Jaipur</p>
      </div>
    </div>
  );
}
